import {useAnalystRunQuery} from '@transactions/graphql';
import * as React from 'react';
import {AnalystChart} from 'src/AnalystView/AnalystChart';
import type {AnalystRows, CardSource, ChartSpec} from 'common/analyst';

/**
 * Executes a card source through analystRun and renders its chart — the
 * shared body of pinned cards and freshly proposed ones. cache-and-network
 * keeps a revisited board current: the source re-runs against live data,
 * which is the whole point of pinning specs instead of results.
 */

const isRows = (value: unknown): value is AnalystRows =>
  Array.isArray(value) &&
  value.every((row) => typeof row === 'object' && row != null);

export const AnalystCardBody = ({
  source,
  chart,
}: {
  source: CardSource;
  chart: ChartSpec;
}) => {
  const {data, loading, error} = useAnalystRunQuery({
    variables: {source},
    fetchPolicy: 'cache-and-network',
  });

  if (error != null) {
    return (
      <div className="text-destructive py-6 text-center text-sm">
        This card failed to refresh: {error.message}
      </div>
    );
  }
  if (data == null && loading) {
    return (
      <div className="text-muted-foreground py-10 text-center text-sm">
        Running…
      </div>
    );
  }
  const rows = data?.analystRun;
  if (!isRows(rows)) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        No rows.
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        Nothing in this window.
      </div>
    );
  }
  return <AnalystChart chart={chart} rows={rows} />;
};
